/**
 * The Inputs view's pricing actions, in two pieces (owner 2026-10-19):
 *
 *   RepricePill  — the floating changes banner (light, content-width, like
 *                  the drilldown's pending-changes affordance). It slides up on
 *                  the FIRST edit with the change count, Review (the list),
 *                  Explain this result, Discard and "Re-price → vN". Nothing
 *                  sits on the page when nothing has changed.
 *
 *   RailActions  — the small card at the top of the results column for the
 *                  states the pill doesn't cover: a blank sizer's first
 *                  "Calculate & price", re-running an UNCHANGED scenario on
 *                  today's sheet, and "Explain this result".
 *
 * A required field still missing blocks the price and says why (in the pill's
 * second line, or under the rail button).
 */
import { useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CalculateOutlined from "@mui/icons-material/CalculateOutlined";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";

import Txt from "../../../components/typography/Txt";
import { status as STATUS } from "../../../theme/colors";
import Flx from "../../../components/layout/Flx";
import {
  SZ,
  SIZER_GUTTER,
  SIZER_MAX_W,
  PrimaryButton,
  SecondaryButton,
} from "../sizerUi";

// The banner's exit slide; the node stays mounted until it finishes.
const EXIT_MS = 320;
// Distance from the window bottom; the page reserves banner height + this + a
// small gap, so the last field clears it without a band of empty page.
export const PILL_BOTTOM = 16;
// PendingChangesBar's palette, so this reads as the same affordance.
const PILL = {
  bg: "#22303c",
  text: "#eef2f5",
  muted: "#c6d0d9",
  amber: "#e8a13c",
};

/* The re-run button: white, a quiet 1px #e6e9ee border, the label in the SAME
   teal as the Save as deal fill. Border + text + background are set together
   (CLAUDE.md button rule). */
const RERUN_SX = {
  width: "100%",
  height: 40,
  minHeight: 40,
  padding: "0 14px",
  justifyContent: "center",
  bgcolor: "#fff",
  color: SZ.teal600,
  border: "1px solid #e6e9ee",
  "& svg": { fontSize: 17, color: SZ.teal600 },
  "&:hover": { bgcolor: SZ.teal50, borderColor: "#e6e9ee", color: SZ.teal700 },
  "&:hover svg": { color: SZ.teal700 },
};

const Spinner = () => <CircularProgress size={16} sx={{ color: "inherit" }} />;

const Note = ({ tone, children }) => (
  <Txt
    component="div"
    sx={{
      fontSize: 12.5,
      lineHeight: 1.5,
      mt: 1,
      color:
        tone === "bad"
          ? SZ.bad
          : tone === "warn"
            ? STATUS.warning.fg
            : SZ.ink400,
      fontWeight: tone === "bad" ? 600 : 400,
    }}
  >
    {children}
  </Txt>
);

/** True when the floating pill is the price action (there IS something priced to change). */
export const pillActive = ({ hasScenario, hasResult, changes }) =>
  (hasScenario || hasResult) && changes.length > 0;

/**
 * The floating changes banner — a light card that slides up from the bottom of
 * the window on the FIRST edit, spans the page's content column (it measures
 * the workspace root, as FixedActionBar does, so it lines up with the cards
 * above it), and slides away once the re-price lands or the edits are
 * discarded.
 *
 *   ┌───────────────────────────────────────────────────────────────────────┐
 *   │ [2] 2 changes since v2            Explain this result  Discard  [Re-price → v3] │
 *   │     Re-pricing saves these as v3 · Review                             │
 *   └───────────────────────────────────────────────────────────────────────┘
 *
 * "Review" lists every change (field · was → now). A missing required field
 * replaces the second line and disables Re-price.
 */
export function RepricePill({
  rootRef,
  hasScenario,
  hasResult,
  shownVersion,
  nextVersion,
  changes = [],
  onPrice,
  onDiscard,
  onExplain,
  pending,
  error,
  blockedReason,
  // Reports the banner's height (0 when gone) so the page reserves exactly
  // that much room under the last field — no more.
  onHeight,
}) {
  const active = pillActive({ hasScenario, hasResult, changes });
  // `mounted` keeps the node through the exit slide; `shown` drives it.
  const [mounted, setMounted] = useState(active);
  const [shown, setShown] = useState(false);
  const [box, setBox] = useState(null);
  const [reviewAnchor, setReviewAnchor] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    if (active) {
      setMounted(true);
      const t = setTimeout(() => setShown(true), 20);
      return () => clearTimeout(t);
    }
    setShown(false);
    setReviewAnchor(null);
    const t = setTimeout(() => setMounted(false), EXIT_MS);
    return () => clearTimeout(t);
  }, [active]);

  // Line up with the content column (both edges, scrollbar included).
  useEffect(() => {
    if (!mounted) return undefined;
    const el = rootRef?.current;
    const measure = () => {
      if (!el) return setBox({ left: 0, right: 0 });
      const r = el.getBoundingClientRect();
      return setBox({
        left: r.left,
        right: Math.max(0, window.innerWidth - r.right),
      });
    };
    measure();
    window.addEventListener("resize", measure);
    const ro =
      el && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    ro?.observe(el);
    return () => {
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [mounted, rootRef]);

  // Tell the page how tall the banner is while it is up.
  useEffect(() => {
    const el = cardRef.current;
    if (!active || !el) {
      onHeight?.(0);
      return undefined;
    }
    const report = () => onHeight?.(el.offsetHeight);
    report();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(report) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, box]);

  if (!mounted || !box) return null;

  const n = changes.length;
  const title = hasScenario
    ? `${n} change${n === 1 ? "" : "s"} since v${shownVersion}`
    : "Inputs changed since this was priced";
  const blocked = Boolean(blockedReason);

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: "fixed",
        left: box.left,
        right: box.right,
        bottom: PILL_BOTTOM,
        zIndex: 90,
        pointerEvents: "none",
      }}
    >
      <Box sx={{ maxWidth: SIZER_MAX_W, mx: "auto", px: SIZER_GUTTER }}>
        {/* The app's dark pending-changes pill (PendingChangesBar's palette,
            radius and amber ping), stretched to the content width. */}
        <Box
          ref={cardRef}
          sx={{
            pointerEvents: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            columnGap: 2,
            rowGap: 1,
            bgcolor: PILL.bg,
            color: PILL.text,
            borderRadius: "16px",
            boxShadow:
              "0 12px 32px rgba(15,23,42,.28), 0 2px 6px rgba(15,23,42,.18)",
            p: "8px 8px 8px 18px",
            transform: shown
              ? "translateY(0)"
              : "translateY(calc(100% + 24px))",
            opacity: shown ? 1 : 0,
            transition:
              "transform .32s cubic-bezier(.2,.9,.3,1.15), opacity .2s ease",
            "@keyframes sizerPillPing": {
              "0%": { transform: "scale(.6)", opacity: 0.7 },
              "100%": { transform: "scale(1.9)", opacity: 0 },
            },
            "@media (prefers-reduced-motion: reduce)": {
              transform: "none",
              transition: "opacity .2s ease",
            },
          }}
        >
          <Flx ac g={1.5} sx={{ minWidth: 0, flex: "1 1 280px" }}>
            {/* amber dot + radar ping while there are unpriced edits */}
            <Box
              aria-hidden
              sx={{
                position: "relative",
                width: 8,
                height: 8,
                borderRadius: "50%",
                flexShrink: 0,
                bgcolor: PILL.amber,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: "-4px",
                  borderRadius: "50%",
                  border: `2px solid ${PILL.amber}`,
                  opacity: 0,
                  animation: "sizerPillPing 2s ease-out infinite",
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                },
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Txt
                component="div"
                sx={{ fontSize: 13.5, fontWeight: 600, color: PILL.text }}
              >
                {title}
              </Txt>
              <Txt
                component="div"
                sx={{
                  fontSize: 12,
                  color: blocked ? PILL.amber : error ? "#ff9b8f" : PILL.muted,
                  opacity: blocked || error ? 1 : 0.85,
                }}
              >
                {blocked
                  ? blockedReason
                  : error
                    ? error.message || String(error)
                    : hasScenario
                      ? `Re-pricing saves these as v${nextVersion}`
                      : "Re-price to update the results"}
                {!blocked && !error && (
                  <>
                    {" · "}
                    <Box
                      component="button"
                      type="button"
                      onClick={(e) => setReviewAnchor(e.currentTarget)}
                      sx={{
                        p: 0,
                        border: 0,
                        background: "none",
                        font: "inherit",
                        fontWeight: 600,
                        color: "#7fd3dd",
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Review
                    </Box>
                  </>
                )}
              </Txt>
            </Box>
          </Flx>

          <Flx
            ac
            g={1}
            sx={{ flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}
          >
            {onExplain && hasResult && (
              <Box
                component="button"
                type="button"
                onClick={onExplain}
                sx={{
                  height: 36,
                  px: "12px",
                  mr: 1,
                  border: 0,
                  borderRadius: "8px",
                  background: "transparent",
                  font: "inherit",
                  fontSize: 13,
                  fontWeight: 600,
                  color: PILL.muted,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    background: "rgba(255,255,255,.10)",
                    color: "#fff",
                  },
                }}
              >
                Explain this result
              </Box>
            )}
            {/* Ghost: no fill, a 1px light border — the secondary way out. */}
            <Box
              component="button"
              type="button"
              onClick={onDiscard}
              disabled={pending}
              sx={{
                height: 36,
                px: "14px",
                border: "1px solid rgba(255,255,255,.22)",
                borderRadius: "8px",
                background: "transparent",
                font: "inherit",
                fontSize: 13,
                fontWeight: 600,
                color: PILL.muted,
                cursor: pending ? "default" : "pointer",
                "&:hover": {
                  background: "rgba(255,255,255,.10)",
                  borderColor: "rgba(255,255,255,.4)",
                  color: "#fff",
                },
                "&:disabled": { opacity: 0.5 },
              }}
            >
              Discard
            </Box>
            <Box
              component="button"
              type="button"
              onClick={onPrice}
              disabled={pending || blocked}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                height: 36,
                px: "16px",
                border: 0,
                borderRadius: "8px",
                background: SZ.teal600,
                font: "inherit",
                fontSize: 13,
                fontWeight: 600,
                color: "#fff",
                cursor: pending || blocked ? "default" : "pointer",
                whiteSpace: "nowrap",
                "&:hover": { background: SZ.teal700 },
                "&:disabled": { opacity: 0.6 },
              }}
            >
              {pending && <Spinner />}
              {pending
                ? "Pricing…"
                : hasScenario
                  ? `Re-price → v${nextVersion}`
                  : "Re-price"}
            </Box>
          </Flx>
        </Box>
      </Box>

      <Popover
        open={Boolean(reviewAnchor)}
        anchorEl={reviewAnchor}
        onClose={() => setReviewAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mb: 1,
              width: 380,
              maxWidth: "calc(100vw - 32px)",
              borderRadius: "10px",
              border: `1px solid ${SZ.line}`,
            },
          },
        }}
      >
        <Box sx={{ p: "10px 14px", borderBottom: `1px solid ${SZ.line}` }}>
          <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: SZ.ink900 }}>
            {title}
          </Txt>
        </Box>
        <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
          {changes.map((c) => (
            <Flx
              key={c.path}
              jb
              g={1.5}
              sx={{
                p: "8px 14px",
                borderBottom: `1px solid ${SZ.line}`,
                "&:last-of-type": { borderBottom: 0 },
              }}
            >
              <Txt sx={{ fontSize: 12.5, color: SZ.ink600, minWidth: 0 }}>
                {c.label}
              </Txt>
              <Txt
                sx={{ fontFamily: SZ.mono, fontSize: 12, whiteSpace: "nowrap" }}
              >
                <Box
                  component="span"
                  sx={{ color: SZ.ink400, textDecoration: "line-through" }}
                >
                  {c.from}
                </Box>
                <Box component="span" sx={{ color: SZ.ink400, mx: 0.5 }}>
                  →
                </Box>
                <Box
                  component="span"
                  sx={{ color: SZ.ink900, fontWeight: 600 }}
                >
                  {c.to}
                </Box>
              </Txt>
            </Flx>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}

export default function RailActions({
  hasScenario,
  shownVersion,
  nextVersion,
  hasResult,
  changes = [],
  onPrice,
  pending,
  error,
  blockedReason,
  liveNote,
  onExplain,
}) {
  const pill = pillActive({ hasScenario, hasResult, changes });
  const needsFirstPrice =
    (!hasScenario && !hasResult) || (hasScenario && !shownVersion);
  const explain =
    onExplain && hasResult ? (
      <Button
        variant="text"
        fullWidth
        startIcon={<HelpOutlineRounded />}
        onClick={onExplain}
        sx={{ fontSize: 13, mt: needsFirstPrice || pill ? 0 : 0.75 }}
      >
        Explain this result
      </Button>
    ) : null;

  // While the banner is up it owns the price action AND Explain.
  if (pill) return null;

  return (
    <Box
      sx={{
        bgcolor: SZ.card,
        border: `1px solid ${SZ.line}`,
        borderRadius: SZ.radius,
        boxShadow: SZ.shadow,
        p: "14px 16px",
      }}
    >
      {needsFirstPrice ? (
        <>
          {hasScenario && (
            <Txt
              component="div"
              sx={{ fontSize: 13.5, fontWeight: 600, color: SZ.ink900, mb: 1 }}
            >
              Not priced yet
            </Txt>
          )}
          <PrimaryButton
            onClick={onPrice}
            disabled={pending || Boolean(blockedReason)}
            startIcon={pending ? <Spinner /> : <CalculateOutlined />}
            sx={{ width: "100%" }}
          >
            {pending ? "Pricing…" : "Calculate & price"}
          </PrimaryButton>
        </>
      ) : (
        <>
          {shownVersion && (
            <Txt
              component="div"
              sx={{
                fontSize: 12.5,
                color: SZ.ink500,
                lineHeight: 1.5,
                mb: 1.25,
              }}
            >
              Edit any input to re-price. You can also re-run v{shownVersion}{" "}
              against today&apos;s rate sheet.
            </Txt>
          )}
          <SecondaryButton
            onClick={onPrice}
            disabled={pending || Boolean(blockedReason)}
            startIcon={pending ? <Spinner /> : <ReceiptLongOutlined />}
            sx={RERUN_SX}
          >
            {pending
              ? "Pricing…"
              : hasScenario
                ? `Re-price on today's sheet → v${nextVersion}`
                : "Re-price"}
          </SecondaryButton>
          {explain}
        </>
      )}
      {blockedReason && <Note tone="warn">{blockedReason}</Note>}
      {error && <Note tone="bad">{error.message || String(error)}</Note>}
      {!blockedReason && !error && needsFirstPrice && liveNote && (
        <Note>{liveNote}</Note>
      )}
    </Box>
  );
}

/**
 * Results that no longer describe the form: dimmed, desaturated, with an amber
 * "Stale — re-price to update" tag. Before this, a user could change inputs and
 * keep reading the old coupon.
 */
export const StaleWrap = ({ stale, children }) => (
  <Box sx={{ position: "relative" }}>
    {stale && (
      <Box
        sx={{
          position: "absolute",
          top: -10,
          right: 12,
          zIndex: 2,
          fontSize: 11,
          fontWeight: 700,
          px: 1,
          py: "2px",
          borderRadius: "99px",
          bgcolor: STATUS.warning.bg,
          color: STATUS.warning.fg,
          border: "1px solid #F5DDB0",
        }}
      >
        Stale — re-price to update
      </Box>
    )}
    <Box
      sx={{
        transition: "opacity .15s, filter .15s",
        ...(stale && { opacity: 0.55, filter: "grayscale(.6)" }),
      }}
    >
      {children}
    </Box>
  </Box>
);
